import { ComponentProps } from 'react';
import { useParams } from 'react-router-dom';
import Alert from '~/components/Alert';
import Card from '~/components/Card';
import Form from '~/components/Form';
import Icon from '~/components/Icon';
import LinkButton from '~/components/LinkButton';
import Submit from '~/components/Submit';
import { useIsLoading } from '~/utils/routing';
import { actionFormKey, useLoaderData } from './orderEdit';

type OrderEditAction = ReturnType<
  typeof useLoaderData
>['orderEditActions'][number];

type ActionType = OrderEditAction['editType'];

type ContentProps = {
  title: string;
  description: string;
  icon: ComponentProps<typeof Icon>['icon'];
  buttonText: string;
  buttonVariant: ComponentProps<typeof LinkButton>['variant'];
  path: 'address' | 'cancel';
};

const orderEditActionContent = {
  UPDATE_SHIPPING_ADDRESS: {
    buttonVariant: 'filled',
    title: 'Update Address',
    description: 'Change the shipping address for your order.',
    icon: 'Truck' as const,
    buttonText: 'Update Address',
    path: 'address',
  },
  CANCEL_ORDER: {
    buttonVariant: 'outlined',
    path: 'cancel',
    icon: 'XMark' as const,
    title: 'Cancel Order',
    description: 'Request to cancel your order.',
    buttonText: 'Cancel',
  },
} satisfies Record<ActionType, ContentProps>;

function EditItem({ action }: { action: OrderEditAction }) {
  const content = orderEditActionContent[action.editType];
  const isLoading = useIsLoading();

  return (
    <li className="flex flex-col items-start gap-x-4 md:flex-row md:items-center md:justify-between">
      <div className="mb-4 flex items-center gap-x-4 md:mb-0">
        <Icon icon={content.icon} className="size-8" />
        <div>
          <h3
            // TODO: revisit these styles
            className="col-start-2 row-start-1 font-medium text-gray-800"
          >
            {content.title}
          </h3>
          <p className="col-start-2 row-start-2 text-xs text-gray-500 md:text-sm">
            {action?.editStartLabel ?? content.description}
          </p>
        </div>
      </div>
      <Form>
        <input
          type="hidden"
          name={actionFormKey}
          value={JSON.stringify(action)}
        />
        <Submit
          loading={isLoading}
          className="w-full shrink-0 md:w-60"
          variant={content.buttonVariant}
        >
          {content.buttonText}
        </Submit>
      </Form>
    </li>
  );
}

// ? maybe useSubmit and have the edit item have an onClick?
export default function EditOrderPage() {
  const { orderEditActions, orderEditExpiredDescriptionText } = useLoaderData();
  const params = useParams();

  const hasActions = orderEditActions.length > 0;

  return (
    <Card>
      {hasActions ?
        <ul className="flex flex-col divide-y divide-outline *:py-4 *:first:pt-0 *:last:pb-0">
          {orderEditActions.map((action) => (
            <EditItem key={action.editType} action={action} />
          ))}
        </ul>
      : <>
          <Alert title="Order Editing Unavailable" variant="info">
            {orderEditExpiredDescriptionText ??
              'This order is no longer able to be edited, please contact us for support.'}
          </Alert>
          <LinkButton
            relative="path"
            to={`/${params.store}/order/${params.idFromPlatform}`}
            className="w-full shrink-0 md:w-60"
          >
            Go Home
          </LinkButton>
        </>
      }
    </Card>
  );
}
